import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import { questionMapping } from '../lib/questionMapping';

const Admin = () => {
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [error, setError] = useState('');
    const [forms, setForms] = useState([]);
    const [selectedForm, setSelectedForm] = useState('');
    const [entries, setEntries] = useState([]);
    const [loading, setLoading] = useState(false);
    const [selectedEntry, setSelectedEntry] = useState(null);
    const [showDetailsModal, setShowDetailsModal] = useState(false);
    const [showExportModal, setShowExportModal] = useState(false);
    const [exportSelectedForms, setExportSelectedForms] = useState(new Set());
    const [exporting, setExporting] = useState(false);

    const handleLogin = async (e) => {
        e.preventDefault();
        setError('');
        setLoading(true);

        try {
            const { data, error } = await supabase
                .rpc('check_admin_credentials', {
                    admin_username: username,
                    admin_password: password
                });

            if (error) {
                console.error('Login error:', error);
                setError('Invalid credentials');
            } else if (data) {
                setIsAuthenticated(true);
                // Store admin session in localStorage
                localStorage.setItem('adminSession', JSON.stringify({
                    isAdmin: true,
                    timestamp: new Date().getTime()
                }));
                loadForms();
            } else {
                setError('Invalid credentials');
            }
        } catch (error) {
            console.error('Login error:', error);
            setError('Login failed');
        } finally {
            setLoading(false);
        }
    };

    const loadForms = async () => {
        try {
            // Add the default form
            setForms(['ai-workshop-mar-2025-london','lcc-june-2025', 'bcc-june-2025', 'ai-transform-2025', 'your-future-digial-workshop-london-2025', 'ideas-fest-uk-2025', 'your-future-digial-workshop-manchester-2025', 'festival-of-entrpreneurs-2025', 'your-future-digial-workshop-london-2026', 'your-future-digial-workshop-dubai-2026',
            'putting-ai-to-work-london-2026', 'putting-ai-to-work-manchester-2026'
            ]);
            setSelectedForm('putting-ai-to-work-manchester-2026');

            // Load entries for the default form
            const { data, error } = await supabase
                .from('workshop_answers')
                .select('*')
                .eq('form_name', 'putting-ai-to-work-manchester-2026')
                .order('updated_at', { ascending: false });

            if (error) throw error;
            setEntries(data || []);
        } catch (error) {
            console.error('Error loading forms:', error);
        }
    };

    const loadEntries = useCallback(async () => {
        if (!selectedForm) return;

        setLoading(true);
        try {
            const { data, error } = await supabase
                .from('workshop_answers')
                .select('*')
                .eq('form_name', selectedForm)
                .order('updated_at', { ascending: false });

            if (error) throw error;
            setEntries(data);
        } catch (error) {
            console.error('Error loading entries:', error);
        } finally {
            setLoading(false);
        }
    }, [selectedForm]);

    useEffect(() => {
        if (selectedForm) {
            loadEntries();
        }
    }, [selectedForm, loadEntries]);

    // Check if already authenticated on component mount
    useEffect(() => {
        const checkAuth = () => {
            const adminSession = localStorage.getItem('adminSession');
            if (adminSession) {
                const session = JSON.parse(adminSession);
                // Check if session is less than 24 hours old
                if (session.isAdmin && (new Date().getTime() - session.timestamp) < 24 * 60 * 60 * 1000) {
                    setIsAuthenticated(true);
                    loadForms();
                } else {
                    localStorage.removeItem('adminSession');
                }
            }
        };
        checkAuth();
    }, []);

    const toggleExportForm = (form) => {
        setExportSelectedForms(prev => {
            const next = new Set(prev);
            if (next.has(form)) next.delete(form);
            else next.add(form);
            return next;
        });
    };

    const handleExportCSV = async () => {
        if (exportSelectedForms.size === 0) return;
        setExporting(true);
        try {
            const { data, error } = await supabase
                .from('workshop_answers')
                .select('*')
                .in('form_name', Array.from(exportSelectedForms))
                .order('form_name', { ascending: true })
                .order('email', { ascending: true });

            if (error) throw error;

            const rows = [['Workshop', 'Email', 'Last Updated', 'Topic', 'Exercise', 'Question', 'Answer']];

            (data || []).forEach(entry => {
                Object.entries(entry.values || {}).forEach(([key, value]) => {
                    const baseKey = key.replace(`${entry.form_name}-`, '');
                    const parts = baseKey.split('-q-');
                    if (parts.length !== 2) return;

                    const topicId = parts[0];
                    const [exerciseIndex, questionIndex] = parts[1].split('-').map(Number);

                    const formData = questionMapping[entry.form_name];
                    const topicData = formData?.[topicId];
                    const exerciseData = topicData?.exercises?.[exerciseIndex];
                    const questionData = exerciseData?.questions?.[questionIndex];

                    const topicName = topicData?.tabName || topicId;
                    const exerciseName = exerciseData
                        ? `Exercise ${exerciseData.exerciseNumber}: ${exerciseData.exerciseName}`
                        : `Exercise ${exerciseIndex + 1}`;
                    const questionText = questionData?.questionText || `Q${questionIndex + 1}`;

                    let answerText = value;
                    if (typeof value === 'string') {
                        try {
                            const parsed = JSON.parse(value);
                            if (Array.isArray(parsed)) answerText = parsed.join('; ');
                        } catch (_) {}
                    }

                    rows.push([
                        entry.form_name,
                        entry.email,
                        new Date(entry.updated_at).toLocaleString(),
                        topicName,
                        exerciseName,
                        questionText,
                        answerText || ''
                    ]);
                });
            });

            const csv = rows
                .map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(','))
                .join('\n');

            const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `workshop-answers-${new Date().toISOString().slice(0, 10)}.csv`;
            link.click();
            URL.revokeObjectURL(url);
            setShowExportModal(false);
        } catch (err) {
            console.error('Export error:', err);
        } finally {
            setExporting(false);
        }
    };

    const handleViewDetails = (entry) => {
        setSelectedEntry(entry);
        setShowDetailsModal(true);
    };

    const handleLogout = () => {
        localStorage.removeItem('adminSession');
        setIsAuthenticated(false);
        setUsername('');
        setPassword('');
    };

    if (!isAuthenticated) {
        return (
            <div className="min-h-screen bg-[#FAF9F6] flex items-center justify-center">
                <div className="bg-white p-8 rounded-lg shadow-md w-full max-w-md">
                    <div className="flex items-center gap-4 mb-6">
                        <img src="/iai-logo.png" alt="Implement AI" className="h-8 w-auto" />
                        <h1 className="text-2xl font-bold text-black">Admin Login</h1>
                    </div>
                    <form onSubmit={handleLogin} className="space-y-4">
                        <div>
                            <label className="block text-black mb-2">Username</label>
                            <input
                                type="text"
                                value={username}
                                onChange={(e) => setUsername(e.target.value)}
                                className="w-full p-2 border rounded-md focus:ring-2 focus:ring-[#8C52FF] focus:border-[#8C52FF]"
                                required
                            />
                        </div>
                        <div>
                            <label className="block text-black mb-2">Password</label>
                            <input
                                type="password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                className="w-full p-2 border rounded-md focus:ring-2 focus:ring-[#8C52FF] focus:border-[#8C52FF]"
                                required
                            />
                        </div>
                        {error && <p className="text-red-500">{error}</p>}
                        <button
                            type="submit"
                            disabled={loading}
                            className="w-full py-2 bg-[#8C52FF] text-white font-semibold rounded-lg hover:bg-[#6B3FCC] transition-colors duration-200 disabled:opacity-50"
                        >
                            {loading ? 'Loading...' : 'Login'}
                        </button>
                    </form>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-[#FAF9F6] p-6">
            <div className="max-w-7xl mx-auto">
                <div className="bg-white rounded-lg shadow-md p-6">
                    <div className="flex justify-between items-center mb-6">
                        <div className="flex items-center gap-4">
                            <img src="/iai-logo.png" alt="Implement AI" className="h-8 w-auto" />
                            <h1 className="text-2xl font-bold text-black">Admin Dashboard</h1>
                        </div>
                        <div className="flex items-center gap-4">
                            <select
                                value={selectedForm}
                                onChange={(e) => setSelectedForm(e.target.value)}
                                className="p-2 border rounded-md focus:ring-2 focus:ring-[#8C52FF] focus:border-[#8C52FF]"
                            >
                                <option value="">Select a form</option>
                                {forms.map((form) => (
                                    <option key={form} value={form}>
                                        {form}
                                    </option>
                                ))}
                            </select>
                            <button
                                onClick={loadForms}
                                className="px-6 py-2 bg-[#8C52FF] text-white font-semibold rounded-lg hover:bg-[#6B3FCC] transition-colors duration-200"
                            >
                                Refresh
                            </button>
                            <button
                                onClick={() => setShowExportModal(true)}
                                className="px-6 py-2 bg-[#8C52FF] text-white font-semibold rounded-lg hover:bg-[#6B3FCC] transition-colors duration-200"
                            >
                                Export CSV
                            </button>
                            <button
                                onClick={handleLogout}
                                className="px-6 py-2 bg-[#8C52FF] text-white font-semibold rounded-lg hover:bg-[#6B3FCC] transition-colors duration-200"
                            >
                                Logout
                            </button>
                        </div>
                    </div>

                    {loading ? (
                        <div className="text-center py-8 text-black">Loading...</div>
                    ) : entries.length > 0 ? (
                        <div className="overflow-x-auto">
                            <table className="min-w-full divide-y divide-gray-200">
                                <thead className="bg-[#FAF9F6]">
                                    <tr>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-black uppercase tracking-wider">
                                            Email
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-black uppercase tracking-wider">
                                            Last Updated
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-black uppercase tracking-wider">
                                            Actions
                                        </th>
                                    </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-200">
                                    {entries.map((entry) => (
                                        <tr key={entry.id}>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-black">
                                                {entry.email}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-black">
                                                {new Date(entry.updated_at).toLocaleString()}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm">
                                                <button
                                                    onClick={() => handleViewDetails(entry)}
                                                    className="text-[#8C52FF] hover:text-[#6B3FCC]"
                                                >
                                                    View Details
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    ) : (
                        <div className="text-center py-8 text-black">
                            {selectedForm ? 'No entries found' : 'Select a form to view entries'}
                        </div>
                    )}
                </div>
            </div>

            {/* Export Modal */}
            {showExportModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white p-6 rounded-lg shadow-xl max-w-lg w-full mx-4">
                        <div className="flex justify-between items-center mb-4">
                            <h3 className="text-xl font-bold text-black">Export to CSV</h3>
                            <button onClick={() => setShowExportModal(false)} className="text-black hover:text-gray-700">✕</button>
                        </div>
                        <p className="text-sm text-gray-600 mb-3">Select the workshops to include in the export:</p>
                        <div className="flex gap-2 mb-3">
                            <button
                                onClick={() => setExportSelectedForms(new Set(forms))}
                                className="px-3 py-1 text-sm bg-[#8C52FF] text-white rounded hover:bg-[#6B3FCC]"
                            >
                                Select All
                            </button>
                            <button
                                onClick={() => setExportSelectedForms(new Set())}
                                className="px-3 py-1 text-sm border border-gray-300 text-gray-600 rounded hover:bg-gray-50"
                            >
                                Deselect All
                            </button>
                        </div>
                        <div className="border rounded-md max-h-64 overflow-y-auto divide-y">
                            {forms.map(form => (
                                <label key={form} className="flex items-center gap-3 px-4 py-2 hover:bg-gray-50 cursor-pointer">
                                    <input
                                        type="checkbox"
                                        checked={exportSelectedForms.has(form)}
                                        onChange={() => toggleExportForm(form)}
                                        className="rounded border-gray-300 text-[#8C52FF] focus:ring-[#8C52FF]"
                                    />
                                    <span className="text-sm text-black">{form}</span>
                                </label>
                            ))}
                        </div>
                        <div className="mt-4 flex justify-between items-center">
                            <span className="text-sm text-gray-500">{exportSelectedForms.size} of {forms.length} selected</span>
                            <div className="flex gap-3">
                                <button
                                    onClick={() => setShowExportModal(false)}
                                    className="px-4 py-2 border border-gray-300 text-gray-600 rounded-lg hover:bg-gray-50"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={handleExportCSV}
                                    disabled={exportSelectedForms.size === 0 || exporting}
                                    className="px-4 py-2 bg-[#8C52FF] text-white rounded-lg hover:bg-[#6B3FCC] disabled:opacity-50"
                                >
                                    {exporting ? 'Exporting...' : 'Export CSV'}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Details Modal */}
            {showDetailsModal && selectedEntry && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-[#FAF9F6] p-6 rounded-lg shadow-xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto">
                        <div className="flex justify-between items-center mb-4">
                            <h3 className="text-xl font-bold text-black">
                                Submission Details - {selectedEntry.email}
                            </h3>
                            <button
                                onClick={() => setShowDetailsModal(false)}
                                className="text-black hover:text-gray-700"
                            >
                                ✕
                            </button>
                        </div>
                        <div className="space-y-4">
                            {Object.entries(selectedEntry.values || {}).map(([key, value]) => {
                                // Remove the form prefix and split the remaining key
                                const baseKey = key.replace(`${selectedForm}-`, '');
                                const parts = baseKey.split('-q-'); // Split on '-q-' to separate topic from question parts

                                // Get the topic ID (e.g., 'ai-helpdesk' or 'data-streams')
                                const topicId = parts[0]; // This will keep hyphens in topic name

                                // Get exercise and question numbers
                                const [exerciseIndex, questionIndex] = parts[1].split('-').map(Number);

                                // Get the topic data from the correct nesting level
                                const formData = questionMapping[selectedForm];
                                const topicData = formData?.[topicId];
                                const exerciseData = topicData?.exercises?.[exerciseIndex];
                                const questionData = exerciseData?.questions?.[questionIndex];

                                return (
                                    <div key={key} className="border-b pb-4">
                                        <div className="text-black mb-2">
                                            <div className="font-medium">
                                                {topicData?.tabName || 'Unknown Topic'} - Exercise {exerciseData?.exerciseNumber || 'Unknown'}
                                            </div>
                                            <div className="font-medium text-sm mb-1">
                                                {exerciseData?.exerciseName || 'Unknown Exercise'}
                                            </div>
                                            <div className="text-sm">
                                                {questionData?.questionText || 'Unknown Question'}
                                            </div>
                                        </div>
                                        <p className="text-black whitespace-pre-wrap mt-2">Answer: {value}</p>
                                    </div>
                                );
                            })}
                        </div>
                        <div className="mt-6 text-right">
                            <button
                                onClick={() => setShowDetailsModal(false)}
                                className="px-4 py-2 bg-[#8C52FF] text-white rounded-lg hover:bg-[#6B3FCC]"
                            >
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Admin; 